import { useState, useEffect } from 'react';
import NewConferenceYearModal from '../modals/NewConferenceYearModal';
import { getPeople, getTeams, getRadioCount, getAcceptedCount, getCheckInStats, invalidateCache } from '../../utils/api';
import { usePassLevels } from '../../hooks/usePassLevels';

export default function AdminOverview({ setCurrentView, showToast }) {
  const passLevels = usePassLevels();
  const [setupOpen, setSetupOpen] = useState(false);
  const [newYearOpen, setNewYearOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [radioCount, setRadioCount] = useState(null);
  // "Rostered" = people who have accepted at least one assignment invite.
  // Acceptances live on BlockPositions, so this comes from a dedicated
  // backend count rather than the People sheet's Status column.
  const [acceptedCount, setAcceptedCount] = useState(null);
  // Live door numbers: app check-ins vs Brushfire check-ins, deduped by
  // individual (both-places people count once, in the app bucket).
  const [ciStats, setCiStats] = useState(null);
  const [ciStatsBusy, setCiStatsBusy] = useState(false);
  const loadCiStats = (refresh) => {
    setCiStatsBusy(true);
    getCheckInStats(refresh)
      .then(d => setCiStats(d || null))
      .catch(() => setCiStats(null)) // pre-redeploy or Brushfire hiccup: section stays quiet
      .finally(() => setCiStatsBusy(false));
  };
  useEffect(() => { loadCiStats(false); }, []);

  useEffect(() => {
    // Always fetch fresh people data so badge counts reflect recent pass level changes
    invalidateCache('people');
    Promise.all([getPeople(), getTeams()])
      .then(([p, t]) => {
        setPeople(p || []);
        setTeams(t || []);
      })
      .catch(err => console.error('Overview load error:', err))
      .finally(() => setLoading(false));
    getRadioCount()
      // Tolerate both response shapes: {count} (current) and a bare number
      // (pre-redeploy backend), so the stat is right either way.
      .then(d => setRadioCount(typeof d === 'number' ? d : (d?.count ?? 0)))
      .catch(() => setRadioCount(0));
    getAcceptedCount()
      .then(d => setAcceptedCount(d?.count ?? 0))
      .catch(() => setAcceptedCount(0));
  }, []);

  const totalPeople = people.length;
  const rosteredPeople = acceptedCount ?? 0;
  const registeredPeople = people.filter(p => p.brushfireId).length;
  const notRostered = totalPeople - rosteredPeople;
  const totalTeams = teams.length;

  const passLevelCounts = passLevels.map(level => ({
    level,
    count: people.filter(p => p.passLevel === level).length,
  }));
  const unassignedPassLevel = people.filter(p => !p.passLevel).length;

  const fmt = (n) => loading ? '—' : n.toLocaleString();

  return (
    <>
      {/* Setup Banner */}
      <div className="setup-banner">
        <div className="setup-banner-inner" onClick={() => setSetupOpen(v => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="setup-progress-ring">
              <svg viewBox="0 0 36 36" style={{ width: 40, height: 40, transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3"/>
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="white" strokeWidth="3"
                  strokeDasharray="97.4" strokeDashoffset="58" strokeLinecap="round"/>
              </svg>
              <span className="setup-ring-label">2/5</span>
            </div>
            <div>
              <div className="setup-banner-title">Finish setting up 2026</div>
              <div className="setup-banner-sub">2 of 5 steps complete · click to see what's left</div>
            </div>
          </div>
          <span className={`setup-chevron${setupOpen ? ' open' : ''}`}>▾</span>
        </div>
        {setupOpen && (
          <div className="setup-checklist">
            {[
              { done: true,  title: 'Build master schedule',              sub: 'Conference sessions, meals, and transport added' },
              { done: true,  title: 'Set conference dates and info',      sub: 'Sept 12–20, 2026 · configured in Settings' },
              { done: false, title: 'Add all teams',                      sub: totalTeams > 0 ? `${totalTeams} teams in system` : 'No teams added yet', action: 'Go →', onClick: () => setCurrentView('teams') },
              { done: false, title: 'Confirm all team leads have logged in', sub: 'Go to Team Lead Access to check status', action: 'Go →', onClick: () => setCurrentView('access') },
              { done: false, title: 'Review meal window rules',            sub: 'Confirm time-to-meal mappings before registration closes', action: 'Go →', onClick: () => setCurrentView('meals') },
            ].map((item, i) => (
              <div key={i} className={`setup-check-item${item.done ? ' done' : ' todo'}`} onClick={item.onClick}>
                <div className={`setup-check-icon${item.done ? ' done-icon' : ' todo-icon'}`}>
                  {item.done
                    ? <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    : <svg viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/></svg>
                  }
                </div>
                <div className="setup-check-body">
                  <div className="setup-check-title">{item.title}</div>
                  <div className="setup-check-sub">{item.sub}</div>
                </div>
                {item.done && <span className="setup-check-status">Done</span>}
                {!item.done && item.action && <span className="setup-check-action">{item.action}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="page-content">
        <div className="page-title-row">
          <div>
            <div className="page-title">Overview</div>
            <div className="page-sub">The Belonging Co Conference · 2026</div>
          </div>
          <button className="btn btn-dark" onClick={() => setNewYearOpen(true)}>+ New Conference Year</button>
        </div>

        {/* Stat Cards — .stat-grid already does repeat(4,1fr) on desktop; an
            inline override here defeated the mobile 2-column rule. */}
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-label">In System</div>
            <div className="stat-value">{fmt(totalPeople)}</div>
            <div className="stat-sub">
              {loading ? '' : registeredPeople > 0 ? `${registeredPeople} registered via Brushfire` : 'No Brushfire registrations yet'}
            </div>
            {!loading && totalPeople > 0 && (
              <div className="stat-bar">
                <div className="stat-bar-fill" style={{ width: `${Math.min(100, (totalPeople / 900) * 100)}%` }} />
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-label">Rostered</div>
            <div className="stat-value">{acceptedCount === null ? '—' : fmt(rosteredPeople)}</div>
            <div className="stat-sub">
              {loading || acceptedCount === null ? '' : notRostered > 0 ? `${notRostered} not yet accepted` : totalPeople === 0 ? 'No people added yet' : 'Everyone accepted'}
            </div>
            {!loading && acceptedCount !== null && totalPeople > 0 && (
              <div className="stat-bar">
                <div className="stat-bar-fill" style={{ width: `${Math.min(100, (rosteredPeople / totalPeople) * 100)}%` }} />
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-label">Teams</div>
            <div className="stat-value">{fmt(totalTeams)}</div>
            <div className="stat-sub">
              {loading ? '' : totalTeams === 0 ? 'Add teams in the Teams tab' : 'Active teams'}
            </div>
            {!loading && totalTeams > 0 && (
              <div className="stat-bar">
                <div className="stat-bar-fill" style={{ width: `${Math.min(100, (totalTeams / 25) * 100)}%` }} />
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-label">Need Radio</div>
            <div className="stat-value">{radioCount === null ? '—' : radioCount}</div>
            <div className="stat-sub">
              {radioCount === null ? '' : radioCount === 0 ? 'No radio flags set' : 'Flagged across all teams'}
            </div>
          </div>
        </div>

        {/* Live check-in numbers — app vs Brushfire, deduped individuals */}
        <div style={{ marginBottom: 24 }}>
          <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            Checked In (Live)
            <button
              className="btn btn-xs"
              onClick={() => loadCiStats(true)}
              disabled={ciStatsBusy}
              style={{ textTransform: 'none', letterSpacing: 0 }}
            >
              {ciStatsBusy ? 'Refreshing…' : '↻ Refresh'}
            </button>
            {ciStats?.asOf && (
              <span style={{ fontFamily: 'var(--font-b)', fontSize: 11, fontWeight: 300, color: 'var(--muted)', textTransform: 'none', letterSpacing: 0 }}>
                as of {new Date(ciStats.asOf).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
          {ciStats && !ciStats.error ? (
            <>
              <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 8 }}>
                <div className="stat-card">
                  <div className="stat-label">Servant Leaders (Our App)</div>
                  <div className="stat-value">{ciStats.app}</div>
                  <div className="stat-sub">checked in at the SL table</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Brushfire Only</div>
                  <div className="stat-value">{ciStats.bfOnly}</div>
                  <div className="stat-sub">checked in via Brushfire, not counted above</div>
                </div>
                <div className="stat-card" style={{ background: 'var(--black)' }}>
                  <div className="stat-label" style={{ color: 'rgba(255,255,255,0.6)' }}>Total Individuals</div>
                  <div className="stat-value" style={{ color: '#fff' }}>{ciStats.total}</div>
                  <div className="stat-sub" style={{ color: 'rgba(255,255,255,0.6)' }}>every person counted once</div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', padding: '10px 0' }}>
              {ciStatsBusy ? 'Loading live check-in numbers…'
                : ciStats?.error ? `Brushfire didn't answer (${ciStats.error}) — app check-ins: ${ciStats.app ?? '—'}. Try Refresh.`
                : 'Live check-in numbers need the latest Apps Script deploy.'}
            </div>
          )}
        </div>

        {/* Pass Level Breakdown */}
        {!loading && totalPeople > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div className="section-header">Pass Level Breakdown</div>
            <div className="pass-breakdown-row" style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
              {/* Pass level counts — one row on desktop; wraps on phones */}
              <div className="panel" style={{ flex: 1, padding: 0, overflow: 'hidden', minWidth: 0 }}>
                <div className="pass-cells" style={{ display: 'flex' }}>
                  {passLevelCounts.map((item, i) => (
                    <div
                      key={item.level}
                      style={{
                        flex: 1, padding: '16px 20px',
                        borderRight: i < passLevelCounts.length - 1 ? '1px solid var(--border)' : 'none',
                        minWidth: 0,
                      }}
                    >
                      <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>
                        {item.level}
                      </div>
                      <div style={{ fontFamily: 'var(--font-h)', fontSize: 26, fontWeight: 800, color: 'var(--black)', lineHeight: 1 }}>
                        {item.count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Unassigned — separate card */}
              {unassignedPassLevel > 0 && (
                <div className="panel" style={{ padding: '16px 20px', flexShrink: 0, background: 'var(--off)' }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>
                    Unassigned
                  </div>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 26, fontWeight: 800, color: 'var(--black)', lineHeight: 1 }}>
                    {unassignedPassLevel}
                  </div>
                  <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 4 }}>
                    no level set
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Two Column */}
        <div className="two-col-equal">
          <div>
            <div className="section-header">Quick Actions</div>
            <div className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
              {[
                { label: 'Manage People.', view: 'people' },
                { label: 'View All Teams.', view: 'teams' },
                { label: 'Master Schedule.', view: 'schedule' },
                { label: 'Meal Counts.', view: 'meals' },
                { label: 'Team Lead Access.', view: 'access' },
              ].map(item => (
                <div key={item.view} className="quick-link" style={{ flex: 1 }} onClick={() => setCurrentView(item.view)}>
                  <span className="quick-link-name">{item.label}</span>
                  <span style={{ color: 'var(--muted)' }}>→</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="section-header">Acceptance Status</div>
            <div className="panel">
              <div className="panel-header">
                <div className="panel-title">
                  {loading || acceptedCount === null ? 'Loading…' : notRostered === 0 && totalPeople === 0
                    ? 'No people in the system yet'
                    : notRostered === 0
                    ? 'Everyone has accepted'
                    : `${notRostered} ${notRostered === 1 ? 'person has' : 'people have'} not yet accepted`}
                </div>
              </div>
              {!loading && acceptedCount !== null && rosteredPeople > 0 && (
                <div style={{ padding: '12px 20px', fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300, borderTop: '1px solid var(--border)' }}>
                  {rosteredPeople} accepted · {notRostered} not yet accepted
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Demo Tools */}
        <div style={{ marginTop: 8 }}>
          <div className="section-header">Demo & Preview</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              { href: '/s/demo', label: 'SL Schedule' },
              { href: '/serve/demo', label: 'SL Availability' },
              { href: '/avail/demo', label: 'Kids Availability' },
              { href: '/checkin/demo', label: 'Check-In View' },
            ].map(item => (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 400,
                  color: 'var(--muted)', textDecoration: 'none',
                  padding: '5px 10px', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', background: 'var(--off)',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--black)'; e.currentTarget.style.borderColor = 'var(--mid)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                {item.label}
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                  <path d="M2 8L8 2M8 2H3.5M8 2v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>

      <NewConferenceYearModal open={newYearOpen} onClose={() => setNewYearOpen(false)} showToast={showToast} />
    </>
  );
}
